import api from '../httpClient';
import apiRoutes from '../apiRoutes';

const unwrapList = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
};

const get = async (url, params) => {
  const response = await api.get(url, { params });
  return response.data;
};

const post = async (url, data, config = {}) => {
  const response = await api.post(url, data, config);
  return response.data;
};

const patch = async (url, data, config = {}) => {
  const response = await api.patch(url, data, config);
  return response.data;
};

const remove = async (url) => api.delete(url);

const fileConfig = { headers: { 'Content-Type': 'multipart/form-data' } };

export const asList = unwrapList;

export const developersApi = {
  listFirms: () => get(apiRoutes.developers.mine).then(unwrapList),
  listVisibleFirms: () => get(apiRoutes.developers.firms).then(unwrapList),
  developerApplicantAuth: (payload) => post(apiRoutes.developers.applicantAuth, payload),
  createFirm: (payload) => post(apiRoutes.developers.firms, payload),
  updateFirm: (id, payload) => patch(apiRoutes.developers.firm(id), payload),
  uploadFirmDocument: (developerId, formData) =>
    post(apiRoutes.developers.firmDocuments(developerId), formData, fileConfig),
  requestBasicVerification: (developerId) =>
    post(apiRoutes.developers.requestBasicVerification(developerId), {}),
  requestMediumVerification: (developerId) =>
    post(apiRoutes.developers.requestMediumVerification(developerId), {}),
  listVerificationCases: (developerId) =>
    get(apiRoutes.developers.verificationCases, developerId ? { developer: developerId } : undefined).then(unwrapList),

  listPortfolioProjects: (developerId) =>
    get(apiRoutes.developers.portfolioProjects, developerId ? { developer: developerId } : undefined).then(unwrapList),
  createPortfolioProject: (payload) => post(apiRoutes.developers.portfolioProjects, payload),
  updatePortfolioProject: (id, payload) => patch(apiRoutes.developers.portfolioProject(id), payload),
  uploadPortfolioDocument: (projectId, formData) =>
    post(apiRoutes.developers.portfolioProjectNestedDocuments(projectId), formData, fileConfig),
  addCustomerReference: (projectId, payload) =>
    post(apiRoutes.developers.portfolioProjectCustomerReferences(projectId), payload),

  listPlatformProjects: (developerId) =>
    get(apiRoutes.developers.platformProjects, developerId ? { developer: developerId } : undefined).then(unwrapList),
  createPlatformProject: (payload) => post(apiRoutes.developers.platformProjects, payload),
  updatePlatformProject: (id, payload) => patch(apiRoutes.developers.platformProject(id), payload),
  getWizardStatus: (projectId) => get(apiRoutes.developers.wizardStatus(projectId)),
  uploadPlatformDocument: (projectId, formData) =>
    post(apiRoutes.developers.platformProjectNestedDocuments(projectId), formData, fileConfig),
  listPlatformDocuments: (projectId) =>
    get(apiRoutes.developers.platformProjectDocuments, projectId ? { project: projectId } : undefined).then(unwrapList),

  listSections: (projectId) => get(apiRoutes.developers.sections, { project: projectId }).then(unwrapList),
  createSection: (payload) => post(apiRoutes.developers.sections, payload),
  deleteSection: (id) => remove(apiRoutes.developers.section(id)),
  listUnitTemplates: (projectId) => get(apiRoutes.developers.unitTemplates, { project: projectId }).then(unwrapList),
  createUnitTemplate: (payload) => post(apiRoutes.developers.unitTemplates, payload),
  deleteUnitTemplate: (id) => remove(apiRoutes.developers.unitTemplate(id)),
  publishUnitTemplateRevision: (revisionId) =>
    post(apiRoutes.developers.publishUnitTemplateRevision(revisionId), {}),
  uploadUnitTemplatePlan: (revisionId, formData) =>
    post(apiRoutes.developers.unitTemplatePlan(revisionId), formData, fileConfig),
  listUnits: (projectId) => get(apiRoutes.developers.units, { project: projectId }).then(unwrapList),
  bulkCreateUnits: (projectId, payload) => post(apiRoutes.developers.bulkCreateUnits(projectId), payload),
  deleteUnit: (id) => remove(apiRoutes.developers.unit(id)),

  listSalesTemplates: (projectId) =>
    get(apiRoutes.developers.salesStrategyTemplates, { project: projectId }).then(unwrapList),
  createSalesTemplate: (payload) => post(apiRoutes.developers.salesStrategyTemplates, payload),
  deleteSalesTemplate: (id) => remove(apiRoutes.developers.salesStrategyTemplate(id)),
  applySalesTemplate: (projectId, payload) => post(apiRoutes.developers.applySalesTemplate(projectId), payload),
  listUnitSalesStrategies: (projectId) =>
    get(apiRoutes.developers.unitSalesStrategies, { project: projectId }).then(unwrapList),
  deleteUnitSalesStrategy: (id) => remove(apiRoutes.developers.unitSalesStrategy(id)),
  createPresaleListings: (projectId, payload) => post(apiRoutes.developers.createPresaleListings(projectId), payload),

  ensureConstructionSchedule: (projectId) => post(apiRoutes.developers.ensureConstructionSchedule(projectId), {}),
  listScheduleVersions: (projectId) =>
    get(apiRoutes.developers.constructionScheduleVersions, { project: projectId }).then(unwrapList),
  createScheduleVersion: (payload) => post(apiRoutes.developers.constructionScheduleVersions, payload),
  updateScheduleVersion: (versionId, payload) => patch(apiRoutes.developers.constructionScheduleVersion(versionId), payload),
  deleteScheduleVersion: (versionId) => remove(apiRoutes.developers.constructionScheduleVersion(versionId)),
  activateScheduleVersion: (versionId) => post(apiRoutes.developers.activateScheduleVersion(versionId), {}),
  listConstructionActivityCatalog: () => get(apiRoutes.developers.constructionActivityCatalog),
  listActivities: (projectId) => get(apiRoutes.developers.constructionActivities, { project: projectId }).then(unwrapList),
  createActivity: (payload) => post(apiRoutes.developers.constructionActivities, payload),

  listSurveyForms: (params) => get(apiRoutes.developers.surveyForms, params).then(unwrapList),
  listSurveyCampaigns: (params) => get(apiRoutes.developers.surveyCampaigns, params).then(unwrapList),
  listSurveyResponses: (params) => get(apiRoutes.developers.surveyResponses, params).then(unwrapList),
  listRatings: () => get(apiRoutes.developers.ratings).then(unwrapList),
  remove
};

export default developersApi;
